import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';

export const getConfiguration = (state) => get(state, 'configurator.configuration', {});

export const getConfigurationCode = (state) => get(state, 'configurator.configuration.code', null);

export const getVariantCode = (state) => {
  const variantCode = String(
    get(state, 'configurator.configuration.customdata.variantCode', '') || ''
  ).trim();

  return variantCode || 'n/a';
};

export const getLanguages = (state) => get(state, 'configurator.languages', []);

export const getSteps = (state) => get(state, 'configurator.steps', []);

export const getItemTitle = (state) => get(state, 'configurator.configuration.item.title', '');

export const getItemDescription = (state) => get(state, 'configurator.configuration.item.description', '');

export const getItemPrice = (state) => get(state, 'configurator.configuration.item.price', '');

export const getStepsState = (state) => get(state, 'configurator.stepsState', []);

export const getTotalFormatted = (state) => get(state, 'configurator.calculation.totalFormatted', '');

export const getItemPriceFormatted = (state) => get(state, 'configurator.calculation.itemPriceFormatted', '');

export const getSurchargePriceFormatted = (state) =>
  get(state, 'configurator.calculation.surchargePriceFormatted', '');

export const getDeliveryTime = (state) => get(state, 'configurator.calculation.deliveryTime');

export const getCalculationSelectedOptions = (state) =>
  get(state, 'configurator.calculation.selectedOptions', []);

export const getActiveStep = (state) => {
  const stepsState = get(state, 'configurator.stepsState');
  const activeStep = find(stepsState, (step) => step.state === 'active');
  return get(activeStep, 'identifier');
};

export const getVisibleComponents = (state) => {
  const components = get(state, 'configurator.configuration.optionclassifications', []);
  return filter(components, (component) => component.hiddenInFrontend !== true);
};

export const getActiveStepComponents = (state) => {
  const steps = getSteps(state);
  const stepsState = getStepsState(state);
  const activeStepStateIdentifier = get(
    find(stepsState, (step) => step.state === 'active'),
    'identifier'
  );
  if (activeStepStateIdentifier) {
    return get(
      find(steps, (step) => step.identifier === activeStepStateIdentifier),
      'optionclassifications',
      []
    );
  }
  return [];
};

export const getNextStepComponents = (state) => {
  const steps = getSteps(state);
  const stepsState = getStepsState(state);
  const activeStepIdx = stepsState.findIndex((step) => step.state === 'active');
  const nextStep = activeStepIdx !== -1 && stepsState[activeStepIdx + 1];
  if (nextStep && nextStep.identifier) {
    return get(
      find(steps, (step) => step.identifier === nextStep.identifier),
      'optionclassifications',
      []
    );
  }
  return [];
};

export const getActiveStepIncompleteComponents = (state) => {
  const components = getVisibleComponents(state);
  const activeStepComponents = getActiveStepComponents(state);
  let incompleteComponents = [];
  for (const identifier of activeStepComponents) {
    const component = find(components, (component) => component.identifier === identifier);
    if (isIncompleteComponent(component)) {
      incompleteComponents.push(component);
    }
  }
  return incompleteComponents;
};

export const getIncompleteComponents = (state) => {
  const components = getVisibleComponents(state);
  let incompleteComponents = [];
  components.forEach((component) => {
    if (isIncompleteComponent(component)) {
      incompleteComponents.push(component);
    }
  });

  return incompleteComponents;
};

export const getGroupedComponents = (state) => {
  const components = getVisibleComponents(state);
  const steps = getSteps(state);
  const groupedComponents = [];
  steps.forEach((step) => {
    const componentGroup = { title: step.title, identifier: step.identifier, components: [] };
    for (const identifier of step.optionclassifications) {
      componentGroup.components.push(
        find(
          components,
          (component) => component.identifier === identifier && component.hiddenInFrontend !== true
        )
      );
    }
    groupedComponents.push(componentGroup);
  });
  return groupedComponents;
};

const isIncompleteComponent = (component) => {
  return (
    component.is_mandatory &&
    (!component.selectedoptions || component.selectedoptions.length === 0) &&
    component.hiddenInFrontend !== true
  );
};

// Mirrors ProductPartsData's isSingleAutoSelected: a component with only one possible
// option that's already selected renders nothing, so it can't be configured further.
const isSingleOptionPreSelected = (component) =>
  get(component, 'optionsCount') === 1 && get(component, 'selectedoptions.length') === 1;

export const stepHasSelectableComponents = (step, components) =>
  get(step, 'optionclassifications', []).some((identifier) => {
    const component = find(components, (c) => c.identifier === identifier);
    return component && !isSingleOptionPreSelected(component);
  });

export const getOptionClassificationOptions = (state) =>
  get(state, 'configurator.optionClassificationOptions', []);

export const getHasVisitedAllSteps = (state) => {
  const stepsState = getStepsState(state);
  const unvisitedStep = find(stepsState, (step) => step.state === 'disabled');
  return !unvisitedStep;
};

export const getCheckedComponents = (state) => Object.keys(state.configurator.checkedOptionclassifications);
